import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid } from '@mui/material';
import { MarketButton, MarketLink, MarketPill } from '@market/react';
import { BUNDLE_OFFER_RESOLUTIONS } from 'constants/software';
import ComparePlansModal from './compare_plans_modal';
import { formatTierMonthlyPrice } from './bundle_offer_utils';

// Inline `+` glyph icon. Square One spans verticals and isn't represented in
// SOFTWARE_GROUPS_BY_BASE_PRODUCT_KEY, so we don't reuse `getSoftwareTypeIcon`
// (which would return null). Matches the blockcell accessory-tile mock and
// keeps the bundle decoupled from the vertical-software icon system.
const PlusIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M12 5v14M5 12h14"
      stroke="#006AFF"
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);

const SelectedOfferCopy = ({
  onOpenComparePlans,
  tier,
  trialDescription,
  t,
}) => (
  <>
    <p className="medium-20" style={{ margin: '0 0 6px', color: '#008C45' }}>
      {t('cart.bundleOffer.activatesAtCheckout')}
    </p>
    <p className="medium-30">
      {t('cart.bundleOffer.selectedTitle', { label: tier.label })}
    </p>
    {trialDescription && <p className="paragraph-20">{trialDescription}</p>}
    <MarketLink className="medium-20" onClick={onOpenComparePlans}>
      {t('cart.bundleOffer.seeAllPlans')}
    </MarketLink>
  </>
);

const RecommendedOfferCopy = ({ tier, onOpenComparePlans, t }) => (
  <>
    <MarketPill
      variant="success"
      size="small"
      style={{
        marginBottom: 8,
        alignSelf: 'flex-start',
        cursor: 'default',
      }}
    >
      {t('cart.bundleOffer.recommended')}
    </MarketPill>
    <p className="medium-30">
      {t('cart.bundleOffer.title', { label: tier.label })}
    </p>
    <p className="paragraph-20">
      {t('cart.bundleOffer.description')}{' '}
      <MarketLink className="medium-20" onClick={onOpenComparePlans}>
        {t('cart.bundleOffer.seeAllPlans')}
      </MarketLink>
    </p>
  </>
);

const BundleOfferAction = ({
  disabled,
  isSelected,
  onAdd,
  onRemove,
  tier,
  t,
}) =>
  isSelected ? (
    <MarketLink
      className="medium-20"
      {...(disabled && { disabled: true })}
      onClick={(e) => {
        e.preventDefault();
        onRemove();
      }}
    >
      {t('cart.label.remove')}
    </MarketLink>
  ) : (
    <MarketButton
      rank="secondary"
      {...(disabled && { disabled: true })}
      onClick={() => onAdd(tier.variant_token)}
    >
      {t('cart.bundleOffer.addCta')}
    </MarketButton>
  );

const priceLineFor = ({ hasTrial, priceLabel, t }) => {
  if (priceLabel === null) return null;
  if (hasTrial) return t('cart.bundleOffer.thenPrice', { price: priceLabel });
  return t('cart.bundleOffer.priceOnly', { price: priceLabel });
};

const BundleOfferCard = ({
  offer,
  selectedTier,
  onAdd = () => {},
  onComparePlansOpen = () => {},
  onRemove = () => {},
  onSelectTier = () => Promise.resolve(),
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [comparePlansOpen, setComparePlansOpen] = useState(false);
  const isSelected = selectedTier !== null && selectedTier !== undefined;

  const handleOpenComparePlans = () => {
    onComparePlansOpen();
    setComparePlansOpen(true);
  };

  const handleSelectTier = async (tierKey) => {
    try {
      await onSelectTier(tierKey);
      setComparePlansOpen(false);
    } catch {
      // Keep the modal open so the seller can retry if the cart update fails.
    }
  };

  if (
    !offer ||
    offer.resolution === BUNDLE_OFFER_RESOLUTIONS.SUPPRESS ||
    // ineligible_to_migrate has no cart surface; the payment step tracks that
    // resolver state without rendering seller-facing messaging.
    offer.resolution === BUNDLE_OFFER_RESOLUTIONS.INELIGIBLE_TO_MIGRATE ||
    (!isSelected && !offer.recommended_tier)
  ) {
    return null;
  }

  const tierKey = isSelected ? selectedTier : offer.recommended_tier;
  const tier = offer.tiers?.[tierKey];
  if (!tier) return null;

  const priceLabel = formatTierMonthlyPrice(tier);
  const trialDays = tier.trial_days;
  const hasTrial = typeof trialDays === 'number' && trialDays > 0;

  const trialLine = hasTrial
    ? t('cart.bundleOffer.trialDays', { trialDays })
    : null;
  const selectedTrialDescription = hasTrial
    ? t('cart.bundleOffer.selectedTrialDescription', { trialDays })
    : null;
  const priceLine = priceLineFor({ hasTrial, priceLabel, t });

  return (
    <Box px={2} py={3} data-testid="bundle-offer-card">
      <Grid container alignItems="center" spacing={2} wrap="nowrap">
        {/* Icon column — fixed-width tile, narrow gutter */}
        <Grid item xs="auto">
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              backgroundColor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PlusIcon />
          </Box>
        </Grid>

        <Grid item xs>
          {isSelected ? (
            <SelectedOfferCopy
              onOpenComparePlans={handleOpenComparePlans}
              tier={tier}
              trialDescription={selectedTrialDescription}
              t={t}
            />
          ) : (
            <RecommendedOfferCopy
              tier={tier}
              onOpenComparePlans={handleOpenComparePlans}
              t={t}
            />
          )}
        </Grid>

        {/* Price + CTA column — flex column, hugs content */}
        <Grid item xs="auto">
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
            gap={1}
          >
            {trialLine && (
              <p className="medium-20" style={{ margin: 0 }}>
                {trialLine}
              </p>
            )}
            {priceLine && (
              <p
                className="paragraph-20"
                style={{ margin: 0, color: '#7B7B7B' }}
              >
                {priceLine}
              </p>
            )}
            <BundleOfferAction
              disabled={disabled}
              isSelected={isSelected}
              onAdd={onAdd}
              onRemove={onRemove}
              tier={tier}
              t={t}
            />
          </Box>
        </Grid>
      </Grid>
      <ComparePlansModal
        open={comparePlansOpen}
        onClose={() => setComparePlansOpen(false)}
        tiers={offer.tiers}
        recommendedTier={offer.recommended_tier}
        selectedTier={selectedTier}
        disabled={disabled}
        onSelectTier={handleSelectTier}
      />
    </Box>
  );
};

export default BundleOfferCard;
